/**
 * What the toolbar project switcher lists (card 1.2), pure so the order and
 * the search are unit-tested: "All projects" and "No project" first, then the
 * personal projects, then one group per team on the roster — VS Code's
 * recent-projects order, JetBrains' grouping.
 *
 * Rows are `{ key, kind: 'all' | 'none' | 'project', project?, label }`; `key`
 * is what `setActiveProject` takes. `flat` is the keyboard order (the rows of
 * every section, top to bottom).
 */
import { ALL_PROJECTS, NO_PROJECT } from './projectScopeFilter';

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
const teamIdOf = (project) => {
  const key = String(project?.scopeKey || '');
  return key.startsWith('team:') ? key.slice('team:'.length) : null;
};

/**
 * @param projects   known projects ({ id, name, color, scopeKey, … })
 * @param query      search text; filters project rows by name (the two fixed rows only when they match too)
 * @param teamName   (teamId) => label for a team's group
 * @param labels     { all, none, personal } display strings
 */
export function buildSwitcherSections({ projects = [], query = '', teamName = (id) => `Team ${id}`, labels = {} } = {}) {
  const q = String(query || '').trim().toLowerCase();
  const matches = (text) => !q || String(text || '').toLowerCase().includes(q);
  const L = { all: 'All projects', none: 'No project', personal: 'Personal', ...labels };

  const fixed = [
    { key: ALL_PROJECTS, kind: 'all', label: L.all },
    { key: NO_PROJECT, kind: 'none', label: L.none },
  ].filter((r) => matches(r.label));

  const personal = [];
  const teams = new Map();
  for (const p of projects) {
    if (!p || p.id == null || !matches(p.name)) continue;
    const row = { key: String(p.id), kind: 'project', project: p, label: p.name || '' };
    const team = teamIdOf(p);
    if (team == null) personal.push(row);
    else {
      if (!teams.has(team)) teams.set(team, []);
      teams.get(team).push(row);
    }
  }
  const sortRows = (rows) => rows.sort((a, b) => byName(a.project, b.project));

  const sections = [];
  if (fixed.length) sections.push({ id: 'scope', label: null, rows: fixed });
  if (personal.length) sections.push({ id: 'personal', label: L.personal, rows: sortRows(personal) });
  for (const [team, rows] of [...teams.entries()].sort((a, b) => String(teamName(a[0])).localeCompare(String(teamName(b[0]))))) {
    sections.push({ id: `team:${team}`, label: teamName(team), rows: sortRows(rows) });
  }
  return { sections, flat: sections.flatMap((s) => s.rows) };
}

/** The trigger's text for the current scope. */
export function switcherLabel({ activeProjectId, activeProject, status }, labels = {}) {
  const L = { all: 'All projects', none: 'No project', loading: 'Project…', ...labels };
  if (activeProjectId === ALL_PROJECTS) return L.all;
  if (activeProjectId === NO_PROJECT) return L.none;
  if (status === 'pending' || !activeProject) return L.loading;
  return activeProject.name || L.loading;
}
